import { useCallback, useEffect, useRef, useState } from 'react';

import { isAllowedGazzettaOrigin } from '../consent-config';
import { IframeMessageType } from '../types';

interface UseGZConsentOptions {
  timeout?: number;
}

interface UseGZConsentResult {
  consentGranted: boolean;
  error: string | null;
  isLoading: boolean;
  requestConsent: () => void;
}

export function useGZConsent({ timeout = 5000 }: UseGZConsentOptions = {}): UseGZConsentResult {
  const [consentGranted, setConsentGranted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      if (!isAllowedGazzettaOrigin(event.origin)) return;

      const data = event.data;
      if (!data || typeof data !== 'object') return;

      if (data.type === IframeMessageType.FP_CONSENT_RESPONSE) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }

        const privacyOK = typeof data.privacyOK === 'boolean' ? data.privacyOK : false;
        setConsentGranted(privacyOK);
        setIsLoading(false);
        setError(null);

        if (typeof window !== 'undefined') {
          window.iframeGZContext = {
            ...window.iframeGZContext,
            consentGranted: privacyOK,
          };
        }
      }

      if (data.type === IframeMessageType.FP_CONSENT_UPDATED) {
        const privacyOK = typeof data.privacyOK === 'boolean' ? data.privacyOK : false;
        setConsentGranted(privacyOK);

        if (typeof window !== 'undefined') {
          window.iframeGZContext = {
            ...window.iframeGZContext,
            consentGranted: privacyOK,
          };
        }
      }
    } catch {
      // Ignore malformed messages
    }
  }, []);

  const requestConsent = useCallback(() => {
    if (typeof window === 'undefined' || !window.parent || window.parent === window) {
      setIsLoading(false);
      setError('Not running in iframe context');
      return;
    }

    setIsLoading(true);
    setError(null);

    window.parent.postMessage({ type: IframeMessageType.FP_CONSENT_REQUEST }, '*');

    timeoutRef.current = setTimeout(() => {
      setIsLoading(false);
      setError('Consent request timed out');
      setConsentGranted(false);
    }, timeout);
  }, [timeout]);

  useEffect(() => {
    window.addEventListener('message', handleMessage, false);
    return () => {
      window.removeEventListener('message', handleMessage, false);
    };
  }, [handleMessage]);

  useEffect(() => {
    requestConsent();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [requestConsent]);

  return {
    consentGranted,
    error,
    isLoading,
    requestConsent,
  };
}
